'use client'

import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'framer-motion'

interface CounterProps {
  value: number
  prefix?: string
  suffix?: string
  className?: string
}

/**
 * Compteur animé des chiffres clés.
 *
 * La valeur finale est rendue immédiatement pour les lecteurs d'écran et
 * lorsque l'utilisateur réduit les animations : l'information n'est jamais
 * dépendante de l'animation.
 */
export function Counter({ value, prefix, suffix, className }: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-40px' })
  const prefersReducedMotion = useReducedMotion()
  const [animated, setAnimated] = useState(0)
  const shouldAnimate = isInView && !prefersReducedMotion

  // Animations réduites : la valeur finale est affichée directement, sans effet.
  const displayed = prefersReducedMotion ? value : animated

  useEffect(() => {
    if (!shouldAnimate) return

    const duration = 1400
    const start = performance.now()
    let frame = 0

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      // Courbe d'easing identique à --ease-out-expo.
      const eased = 1 - Math.pow(1 - progress, 4)
      setAnimated(Math.round(value * eased))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [shouldAnimate, value])

  const formatted = new Intl.NumberFormat().format(displayed)
  const finalValue = `${prefix ?? ''}${new Intl.NumberFormat().format(value)}${suffix ?? ''}`

  return (
    <span ref={ref} className={className}>
      <span aria-hidden="true">
        {prefix}
        {formatted}
        {suffix}
      </span>
      <span className="sr-only">{finalValue}</span>
    </span>
  )
}
